import * as Sentry from '@sentry/capacitor';
import * as SentryReact from '@sentry/react';
import { useEffect } from 'react';
import { useLocation, useNavigationType, createRoutesFromChildren, matchRoutes } from 'react-router';
import { isSupportedBrowser } from 'vite-plugin-supported-browserslist/web';
import { isAxiosError } from 'axios';

import { isNativePlatform } from 'services/auth/platform';

const handleBeforeSend = (event: SentryReact.ErrorEvent, hint?: SentryReact.EventHint) => {
  // Ignore Axios errors (they are captured by the httpClientIntegration).
  if (isAxiosError(hint?.originalException)) {
    return null;
  }

  // Process all other events normally
  return event;
};

/**
 * Initialises Sentry through the Capacitor wrapper so native (iOS/Android) hard crashes are captured,
 * with the React SDK as the sibling that powers the JS layer (integrations, routing, replay).
 *
 * The DSN is per-tenant, so on native this is called after the runtime tenant config is hydrated (passing the selected instance's DSN),
 * and an instance switch reloads the WebView to re-init against the new DSN.
 * On web it is called at import time with the build-time env DSN.
 */
export const initSentry = (
  dsn: string | undefined = import.meta.env.VITE_SENTRY_DSN,
  environment: string | undefined = import.meta.env.VITE_APP_ENV
): void => {
  Sentry.init(
    {
      dsn,

      // The native layer owns its own offline crash queue, so the browser offline transport is web-only.
      transport: isNativePlatform() ? undefined : SentryReact.makeBrowserOfflineTransport(SentryReact.makeFetchTransport),

      environment: environment ?? 'local',

      release: import.meta.env.VITE_COMMIT_HASH_COMPLETE,

      // Adds request headers and IP for users, for more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
      sendDefaultPii: true,

      normalizeDepth: 5,
      maxValueLength: 2500,

      integrations: [
        SentryReact.extraErrorDataIntegration(),
        SentryReact.eventFiltersIntegration(),
        SentryReact.reactRouterV7BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes
        }),
        SentryReact.browserProfilingIntegration(),
        SentryReact.browserSessionIntegration(),
        SentryReact.httpClientIntegration(),
        SentryReact.consoleLoggingIntegration({
          levels: ['log', 'warn', 'error']
        }),
        SentryReact.replayIntegration({
          networkDetailAllowUrls: [/^https?:\/\/(localhost|.*\.atlasone\.co)\/api\//]
        })
      ],

      ignoreErrors: [
        'CanceledError',
        'Request aborted',
        'Access token expired',
        'Event `Event` (type=error) captured as promise rejection',
        'Invalid time value'
      ],

      beforeSend: handleBeforeSend,

      // Enable logs to be sent to Sentry
      enableLogs: true,

      // https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
      tracesSampleRate: parseFloat(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE ?? '0.0'),

      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      tracePropagationTargets: [import.meta.env.VITE_APP_DOMAIN ?? 'localhost', /\.atlasone\.co\/api\//],

      // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
      // @ts-expect-error Capacitor SDK doesn't support replay integration but passes it through to the React SDK.
      replaysSessionSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE ?? '0.05'),
      replaysOnErrorSampleRate: parseFloat(import.meta.env.VITE_SENTRY_REPLAY_ERROR_SAMPLE_RATE ?? '1.0'),

      // 100% of sampled sessions will have a profile attached to it. The profile sample rate is applied *after* the trace sample rate.
      profileSessionSampleRate: 1,
      profileLifecycle: 'trace'
    },
    SentryReact.init
  );

  Sentry.setTag('supported_browser', isSupportedBrowser());
};

// Web reads the DSN from the build-time env and can init immediately.
// Native defers to index.jsx, which calls initSentry with the selected tenant's DSN after the runtime config is hydrated.
if (!isNativePlatform()) {
  initSentry();
}
